<div mat-dialog-title class="stock-dialog__header" data-test="dialog">
  <div class="stock-dialog__heading">
    <div class="stock-dialog__title text--bold">{{ data.title }}</div>
    <div class="stock-dialog__subtitle text--small text--gray">{{ data.subTitle }}</div>
  </div>
  <button
    type="button"
    mat-icon-button
    class="stock-dialog__close"
    data-test="dialog-close"
    [attr.aria-label]="'close' | translate"
    (click)="cancel()"
  >
    <mat-icon>clear</mat-icon>
  </button>
</div>

<mat-dialog-content class="stock-dialog__content">
  <div class="stock-dialog__field-group">
    <label class="stock-dialog__label" for="current-stock-crop-year">
      {{ 'shared.stockEntry.cropYear' | translate }}
    </label>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-select
        id="current-stock-crop-year"
        data-test="current-stock-crop-year"
        [formControl]="cropYearControl"
        (selectionChange)="requestYearChange($event.value)"
      >
        @for (year of availableYears; track year) {
          <mat-option [value]="year">{{ year }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  <div class="stock-dialog__field-group">
    <label class="stock-dialog__label" for="current-stock-product-search">
      {{ 'shared.stockEntry.searchProduct' | translate }}
    </label>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-icon matPrefix>search</mat-icon>
      <input
        id="current-stock-product-search"
        type="text"
        matInput
        autocomplete="off"
        data-test="current-stock-product-search"
        [formControl]="searchControl"
        [matAutocomplete]="productAutocomplete"
        [placeholder]="'shared.stockEntry.searchPlaceholder' | translate"
      />
      <mat-autocomplete
        #productAutocomplete="matAutocomplete"
        [displayWith]="displayProduct"
        (optionSelected)="onProductSelected($event)"
      >
        @for (product of filteredProducts(); track product.id) {
          <mat-option [value]="product" [attr.data-test]="'current-stock-product-option-' + product.id">
            {{ formatProduct(product) }}
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  </div>

  <h3 class="stock-dialog__section-title">{{ 'shared.stockEntry.productsInStock' | translate }}</h3>

  @if (loading()) {
    <div class="stock-dialog__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else {
    <div class="stock-dialog__products" data-test="current-stock-products">
      @for (row of stockRows(); track row.product.id) {
        <div
          class="stock-dialog__product-row"
          [class.stock-dialog__product-row--highlighted]="highlightedProductId() === row.product.id"
          [attr.data-stock-product-id]="row.product.id"
        >
          <div class="stock-dialog__product-name">
            <strong>{{ row.product.name }}</strong>
            @if (row.product.admissionNumbers.length > 0) {
              <span>({{ row.product.admissionNumbers.join(', ') }})</span>
            }
            @if (row.product.unitOfMeasurement) {
              <span>({{ row.product.unitOfMeasurement }})</span>
            }
          </div>

          <div class="stock-dialog__quantity">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <input
                matInput
                type="number"
                inputmode="decimal"
                min="0"
                step="0.01"
                [formControl]="row.quantity"
                [matTooltip]="'shared.stockEntry.minimumAmountError' | translate"
                [matTooltipDisabled]="row.quantity.value === null || row.quantity.valid"
                [attr.aria-label]="'shared.stockEntry.quantity' | translate"
                [attr.data-test]="'current-stock-quantity-' + row.product.id"
              />
            </mat-form-field>
            <span class="stock-dialog__unit">{{ row.product.unitOfMeasurement }}</span>
          </div>

          <button
            type="button"
            mat-icon-button
            class="stock-dialog__remove"
            [attr.aria-label]="'shared.stockEntry.removeProduct' | translate: { product: row.product.name }"
            [attr.data-test]="'current-stock-remove-' + row.product.id"
            (click)="removeProduct(row.product.id)"
          >
            <mat-icon>clear</mat-icon>
          </button>
        </div>
      }
    </div>
  }
</mat-dialog-content>

<mat-dialog-actions class="stock-dialog__actions">
  <span data-test="current-stock-product-count">
    {{ productCountTranslationKey() | translate: { count: stockRows().length.toString() } }}
  </span>
  <div class="stock-dialog__action-buttons">
    <button type="button" mat-button data-test="dialog-cancel" (click)="cancel()">
      {{ data.cancelButtonText }}
    </button>
    <span
      class="stock-dialog__save-control"
      [matTooltip]="isPastCropYear() ? ('UI.CurrentYearOnlyMutationMessage' | translate) : ''"
      [matTooltipDisabled]="!isPastCropYear()"
    >
      <button
        type="button"
        mat-flat-button
        color="primary"
        data-test="dialog-submit"
        [disabled]="isSaveDisabled()"
        (click)="saveStock()"
      >
        {{ data.confirmButtonText }}
      </button>
    </span>
  </div>
</mat-dialog-actions>
